import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { isArray } from 'lodash';
import { EMPTY, Observable, expand, forkJoin, of, reduce } from 'rxjs';
import { CompanyVendor } from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { GridService } from 'src/app/services/grid.service';
import { UtilityService } from 'src/app/services/utility.service';

import { BaseGridComponent } from '../base-grid/base-grid.component';

// Rows per request when the grid loads every row for an export. The API builds
// and serializes each response on its one thread, so a single request for a
// large client's whole vendor list stalls every other request on that task.
const EXPORT_PAGE_SIZE = 250;

@Component({
  selector: 'app-company-vendors-grid',
  templateUrl: '../base-grid/base-grid.component.html',
  styleUrls: [
    './company-vendors-grid.component.less',
    '../base-grid/base-grid.component.less'
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CompanyVendorsGridComponent
  extends BaseGridComponent
  implements OnInit
{
  constructor(
    protected override GridService: GridService,
    protected override UtilityService: UtilityService,
    protected override AuthService: AuthService,
    private CompanyVendorService: CompanyVendorService
  ) {
    super(GridService, UtilityService, AuthService);

    this.gridPrimaryKeyFieldName = 'id';
    this.gridType = 'CompanyVendor';
    this.rowModelType = 'serverSide';

    this.additionalQueryData = {
      attributes: [
        'id',
        'CompanyId',
        'VendorId',
        'ExemptVendorId',
        'ClientRegisteredVendorId',
        'CompanyComplianceTypeId'
      ],
      Company: {
        attributes: ['id'],
        options: { required: true }
      },
      Vendor: {
        options: { required: false },
        attributes: ['id', 'legalName']
      },
      ExemptVendor: {
        options: { required: false },
        attributes: ['id', 'legalName']
      },
      ClientRegisteredVendor: {
        options: { required: false },
        attributes: ['id', 'legalName']
      }
    };

    this.ServerSideDataSource = {
      getRows: (params: IServerSideGetRowsParams) => {
        const { request } = params;

        const queryObject = this.constructGridQuery({ request });
        const countQueryObject = this.constructGridQuery(
          { request },
          { isCountQuery: true }
        );

        const { pivotCols, pivotMode, rowGroupCols, valueCols } = request;

        // Grouped, aggregated and pivoted results have no stable row order to
        // page by, so their exports keep one request.
        const pageExport =
          (this.loadAllData || this.loadAllRows) &&
          rowGroupCols.length === 0 &&
          valueCols.length === 0 &&
          !pivotMode;

        (() => {
          if (pivotCols.length > 0 && pivotMode) {
            // We may be able to supply this data to the query below instead of double-querying for
            // pivot column information.
            return this.CompanyVendorService.getCompanyVendorPivotColumnHeaders(
              queryObject
            );
          }
          return of(null);
        })().subscribe({
          next: (pivotColumnHeaders: null | string[]) => {
            forkJoin([
              pageExport
                ? this.getCompanyVendorsInPages(queryObject)
                : this.CompanyVendorService.getCompanyVendors(queryObject),
              this.CompanyVendorService.getCompanyVendorCount(countQueryObject)
            ]).subscribe({
              next: ([CompanyVendors, companyVendorCount]) => {
                const successObject: {
                  rowData: any[];
                  pivotResultFields?: string[];
                  rowCount: number;
                } = {
                  rowData: CompanyVendors,
                  rowCount: companyVendorCount
                };

                if (isArray(pivotColumnHeaders)) {
                  Object.assign(successObject, {
                    pivotResultFields: pivotColumnHeaders
                  });
                }

                params.success(successObject);
              },
              error: (error) => {
                console.log(error);
                params.fail();
              }
            });
          }
        });
      }
    };
  }

  // An export reload asks for every row in one query with no limit. Fetch the
  // rows in pages, one request after another, and hand the grid all of them in
  // a single success call. Offset paging needs a total order or a row can
  // repeat or vanish between pages, so the primary key goes last.
  private getCompanyVendorsInPages(
    queryObject: GenericObject
  ): Observable<CompanyVendor[]> {
    const primaryKey = this.gridPrimaryKeyFieldName || 'id';
    const options: GenericObject = queryObject['options']
      ? JSON.parse(queryObject['options'])
      : {};
    const order: string[][] = queryObject['order']
      ? JSON.parse(queryObject['order'])
      : [];

    const pageQuery: GenericObject = { ...queryObject };
    if (!order.some(([field]) => field === primaryKey)) {
      pageQuery['order'] = JSON.stringify([...order, [primaryKey, 'ASC']]);
    }

    let offset = 0;
    const getPage = (): Observable<CompanyVendor[]> => {
      const page = this.CompanyVendorService.getCompanyVendors({
        ...pageQuery,
        options: JSON.stringify({ ...options, limit: EXPORT_PAGE_SIZE, offset })
      });
      offset += EXPORT_PAGE_SIZE;
      return page;
    };

    return getPage().pipe(
      expand((rows) => (rows.length === EXPORT_PAGE_SIZE ? getPage() : EMPTY)),
      reduce(
        (allRows: CompanyVendor[], rows: CompanyVendor[]) =>
          allRows.concat(rows),
        []
      )
    );
  }
}
